import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ReviewItem } from "@/types/review";
import { useTranslations } from "use-intl";
import { useFormatRelativeDate } from "@/i18n/useFormatRelativeDate";
import { useReviewLabels } from "./useReviewLabels";

interface ReviewTableProps {
  items: ReviewItem[];
  onReviewClick: (item: ReviewItem) => void;
  formatCurrency: (amount: number, currency: string) => string;
}

const ReviewTable: React.FC<ReviewTableProps> = ({
  items,
  onReviewClick,
  formatCurrency,
}) => {
  const t = useTranslations("consultantReview.table");
  const labels = useReviewLabels();
  const formatRelativeDate = useFormatRelativeDate();
  const getReviewTypeBadgeVariant = (reviewType: string) => {
    switch (reviewType) {
      case 'choice':
        return 'default'; // blue
      case 'context':
        return 'secondary'; // yellow/warning
      case 'approve':
        return 'outline'; // green
      default:
        return 'outline';
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    if (status === 'completed') return 'default';
    if (status === 'needs_review') return 'destructive';
    return 'secondary';
  };

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("columns.title")}</TableHead>
            <TableHead>{t("columns.type")}</TableHead>
            <TableHead>{t("columns.reviewType")}</TableHead>
            <TableHead>{t("columns.amount")}</TableHead>
            <TableHead>{t("columns.status")}</TableHead>
            <TableHead>{t("columns.created")}</TableHead>
            <TableHead>{t("columns.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length > 0 ? (
            items.map((item) => (
              <TableRow
                key={item.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onReviewClick(item)}
              >
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {item.title}
                    {item.actionSource === 'system' && (
                      <Badge variant="secondary" className="bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-700/10">
                        {t("auto")}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {labels.itemType(item.type)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={getReviewTypeBadgeVariant(item.reviewType)}>
                    {labels.reviewType(item.reviewType)}
                  </Badge>
                </TableCell>
                <TableCell>
                  {item.amount && item.currency ? (
                    item.sekAmount != null && item.currency !== 'SEK' ? (
                      <div>
                        <div>{formatCurrency(item.sekAmount, 'SEK')}</div>
                        <div className="text-xs text-muted-foreground">
                          {formatCurrency(item.amount, item.currency)}
                        </div>
                      </div>
                    ) : (
                      formatCurrency(item.amount, item.currency)
                    )
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={getStatusBadgeVariant(item.status)}
                    className={item.status === 'needs_review' ? 'bg-amber-100 text-amber-800 hover:bg-amber-100/80' : undefined}
                  >
                    {labels.status(item.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatRelativeDate(item.createdAt)}
                </TableCell>
                <TableCell>
                  {item.status === 'pending' || item.status === 'needs_review' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onReviewClick(item);
                      }}
                    >
                      {t("review")}
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onReviewClick(item);
                      }}
                    >
                      {t("view")}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7} className="h-24 text-center">
                {t("empty")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ReviewTable;
